<template>
  <div class="space-y-4">
    <div class="flex gap-2 justify-end items-center">
      <div class="flex gap-2 items-center">
        <BaseButton
          type="button"
          small
          outline
          primary
          v-if="isFirst === false"
          @click="moveSubscriptionPlan('up')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
          </svg>
        </BaseButton>
        <BaseButton
          type="button"
          small
          outline
          primary
          v-if="isLast === false"
          @click="moveSubscriptionPlan('down')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="w-4 h-4"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </BaseButton>
      </div>
      <BaseButton type="button" small outline danger @click="removeSubscriptionPlan">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-4 h-4"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
          />
        </svg>
      </BaseButton>
    </div>
    <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
      <div class="sm:pt-2">
        <BaseFormLabel :for="`${vidPrefix}subscription_period_interval`">Interval</BaseFormLabel>
      </div>
      <div class="sm:col-span-2 flex gap-2 items-center">
        <div class="flex gap-2 items-center">
          <BaseFormSelectFieldWithValidator
            v-model="subscriptionPeriodIntervalInput"
            v-bind="subscriptionPeriodIntervalFieldProps"
            :disabled="disabled"
          />
          <BaseFormSelectFieldWithValidator
            v-model="subscriptionPeriodInput"
            v-bind="subscriptionPeriodFieldProps"
            :disabled="disabled"
          />
        </div>
        <ul v-if="intervalErrors.length" class="space-y-1 mt-2">
          <li v-for="(msg, msgIdx) in intervalErrors" :key="msgIdx">
            <BaseFormInlineMessage danger>
              {{ msg }}
            </BaseFormInlineMessage>
          </li>
        </ul>
      </div>
    </div>
    <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
      <div class="sm:pt-2">
        <BaseFormLabel :for="`${vidPrefix}subscription_length`">Length</BaseFormLabel>
      </div>
      <div class="sm:col-span-2">
        <BaseFormSelectFieldWithValidator
          v-model="subscriptionLengthInput"
          v-bind="subscriptionLengthFieldProps"
          :disabled="disabled"
        />
        <ul v-if="lengthErrors.length" class="space-y-1 mt-2">
          <li v-for="(msg, msgIdx) in lengthErrors" :key="msgIdx">
            <BaseFormInlineMessage danger>
              {{ msg }}
            </BaseFormInlineMessage>
          </li>
        </ul>
      </div>
    </div>
    <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
      <div class="sm:pt-2">
        <BaseFormLabel :for="`${vidPrefix}override_product_price`">Price</BaseFormLabel>
      </div>
      <div class="sm:col-span-2 space-y-6">
        <div>
          <BaseFormSelectFieldWithValidator
            v-model="overrideProductPriceInput"
            v-bind="overrideProductPriceFieldProps"
            :disabled="disabled"
          />
        </div>
        <div v-if="overrideProductPriceInput">
          <BaseFormTextFieldWithValidator
            v-model="subscriptionPriceInput"
            v-bind="subscriptionPriceFieldProps"
            :disabled="disabled"
            class="w-24"
          />
          <ul v-if="priceErrors.length" class="space-y-1 mt-2">
            <li v-for="(msg, msgIdx) in priceErrors" :key="msgIdx">
              <BaseFormInlineMessage danger>
                {{ msg }}
              </BaseFormInlineMessage>
            </li>
          </ul>
        </div>
        <div v-else>
          <BaseFormTextFieldWithValidator
            v-model="subscriptionPriceDiscountInput"
            v-bind="subscriptionPriceDiscountFieldProps"
            :disabled="disabled"
            class="w-36"
          />
          <ul v-if="priceDiscountErrors.length" class="space-y-1 mt-2">
            <li v-for="(msg, msgIdx) in priceDiscountErrors" :key="msgIdx">
              <BaseFormInlineMessage danger>
                {{ msg }}
              </BaseFormInlineMessage>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { pick } from 'lodash-es';

export default {
  props: {
    vidPrefix: {
      type: String,
      default: '',
    },
    subscriptionPlan: {
      type: Object,
      default: () => ({}),
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    disabled: Boolean,
    isFirst: Boolean,
    isLast: Boolean,
  },
  computed: {
    overrideProductPriceInput: {
      get() {
        return this.subscriptionPlan.overrideProductPrice;
      },
      set(overrideProductPrice) {
        this.$emit('update', {
          id: this.subscriptionPlan.id,
          attributes: {
            overrideProductPrice,
            subscriptionPrice: '',
            subscriptionPriceDiscount: '',
          },
        });
      },
    },
    subscriptionPriceInput: {
      get() {
        return this.subscriptionPlan.subscriptionPrice;
      },
      set(subscriptionPrice) {
        this.$emit('update', {
          id: this.subscriptionPlan.id,
          attributes: {
            subscriptionPrice,
          },
        });
      },
    },
    subscriptionPriceDiscountInput: {
      get() {
        return this.subscriptionPlan.subscriptionPriceDiscount;
      },
      set(subscriptionPriceDiscount) {
        this.$emit('update', {
          id: this.subscriptionPlan.id,
          attributes: {
            subscriptionPriceDiscount,
          },
        });
      },
    },
    subscriptionPeriodInput: {
      get() {
        return this.subscriptionPlan.subscriptionPeriod;
      },
      set(subscriptionPeriod) {
        this.$emit('update', {
          id: this.subscriptionPlan.id,
          attributes: {
            subscriptionPeriod,
          },
        });
      },
    },
    subscriptionPeriodIntervalInput: {
      get() {
        return this.subscriptionPlan.subscriptionPeriodInterval;
      },
      set(subscriptionPeriodInterval) {
        this.$emit('update', {
          id: this.subscriptionPlan.id,
          attributes: {
            subscriptionPeriodInterval,
          },
        });
      },
    },
    subscriptionLengthInput: {
      get() {
        return this.subscriptionPlan.subscriptionLength;
      },
      set(subscriptionLength) {
        this.$emit('update', {
          id: this.subscriptionPlan.id,
          attributes: {
            subscriptionLength,
          },
        });
      },
    },
    overrideProductPriceFieldProps() {
      return {
        validator: {
          name: 'override product price',
          vid: `${this.vidPrefix}override_product_price`,
          rules: { required: true },
        },
        field: {
          id: `${this.vidPrefix}override_product_price`,
          options: [
            { label: 'Inherit from product', value: 0 },
            { label: 'Override product price', value: 1 },
          ],
          required: true,
        },
        withoutInlineErrorMessage: true,
      };
    },
    subscriptionPriceFieldProps() {
      return {
        validator: {
          name: 'subscription price',
          vid: `${this.vidPrefix}subscription_price`,
          rules: { required: true },
        },
        field: {
          id: `${this.vidPrefix}subscription_price`,
          label: 'Amount',
          inputType: 'number',
          attrs: {
            min: '0.50',
            step: '0.01',
          },
        },
        withoutInlineErrorMessage: true,
      };
    },
    subscriptionPriceDiscountFieldProps() {
      return {
        validator: {
          name: 'subscription price discount',
          vid: `${this.vidPrefix}subscription_price_discount`,
          rules: { required: false },
        },
        field: {
          id: `${this.vidPrefix}subscription_price_discount`,
          label: 'Discount %',
          inputType: 'number',
          attrs: {
            min: '0',
            step: '0.01',
          },
        },
        withoutInlineErrorMessage: true,
      };
    },
    subscriptionPeriodFieldProps() {
      return {
        validator: {
          name: 'subscription period',
          vid: `${this.vidPrefix}subscription_period`,
          rules: { required: true },
        },
        field: {
          id: `${this.vidPrefix}subscription_period`,
          options: [
            { label: 'month', value: 'month' },
            { label: 'year', value: 'year' },
          ],
          required: true,
        },
        withoutInlineErrorMessage: true,
      };
    },
    subscriptionPeriodIntervalFieldProps() {
      return {
        validator: {
          name: 'subscription interval',
          vid: `${this.vidPrefix}subscription_period_interval`,
          rules: { required: true },
        },
        field: {
          id: `${this.vidPrefix}subscription_period_interval`,
          options: [
            { label: 'every', value: 1 },
            { label: 'every 2nd', value: 2 },
            { label: 'every 3rd', value: 3 },
            { label: 'every 4th', value: 4 },
            { label: 'every 5th', value: 5 },
            { label: 'every 6th', value: 6 },
          ],
          required: true,
        },
        withoutInlineErrorMessage: true,
      };
    },
    subscriptionLengthFieldProps() {
      return {
        validator: {
          name: 'subscription length',
          vid: `${this.vidPrefix}subscription_length`,
          rules: { required: false },
        },
        field: {
          id: `${this.vidPrefix}subscription_length`,
          options: [
            { label: 'Never expire', value: null },
            { label: `3 ${this.subscriptionPlan.subscriptionPeriod}s`, value: 3 },
            { label: `6 ${this.subscriptionPlan.subscriptionPeriod}s`, value: 6 },
            { label: `9 ${this.subscriptionPlan.subscriptionPeriod}s`, value: 9 },
            { label: `12 ${this.subscriptionPlan.subscriptionPeriod}s`, value: 12 },
            { label: `15 ${this.subscriptionPlan.subscriptionPeriod}s`, value: 15 },
            { label: `18 ${this.subscriptionPlan.subscriptionPeriod}s`, value: 18 },
            { label: `21 ${this.subscriptionPlan.subscriptionPeriod}s`, value: 21 },
            { label: `24 ${this.subscriptionPlan.subscriptionPeriod}s`, value: 24 },
          ],
          required: true,
        },
        withoutInlineErrorMessage: true,
      };
    },
    intervalErrors() {
      return Object.values(
        pick(this.errors, [
          `${this.vidPrefix}subscription_period`,
          `${this.vidPrefix}subscription_period_interval`,
        ])
      ).flat();
    },
    lengthErrors() {
      return Object.values(pick(this.errors, [`${this.vidPrefix}subscription_length`])).flat();
    },
    priceErrors() {
      return Object.values(pick(this.errors, [`${this.vidPrefix}subscription_price`])).flat();
    },
    priceDiscountErrors() {
      return Object.values(
        pick(this.errors, [`${this.vidPrefix}subscription_price_discount`])
      ).flat();
    },
  },
  methods: {
    removeSubscriptionPlan() {
      this.$emit('remove', this.subscriptionPlan.id);
    },
    moveSubscriptionPlan(direction) {
      this.$emit('move', { id: this.subscriptionPlan.id, direction });
    },
  },
};
</script>
